import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GitDiff, ArrowRight, Sparkle, Warning, Lightbulb, Users } from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Disclaimer } from "@/components/Disclaimer";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const [a, setA] = useState(params.get("a") || "");
  const [b, setB] = useState(params.get("b") || "");
  const [na, setNa] = useState(params.get("na") || "");
  const [nb, setNb] = useState(params.get("nb") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async (ra, rb, lna, lnb) => {
    if (!ra || !rb) return;
    setLoading(true); setError(""); setData(null);
    try {
      const { data } = await api.get("/compare", { params: { a: ra, b: rb, na: lna || undefined, nb: lnb || undefined } });
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (params.get("a") && params.get("b")) run(params.get("a"), params.get("b"), params.get("na"), params.get("nb"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setParams({ a, b, ...(na ? { na } : {}), ...(nb ? { nb } : {}) });
    run(a, b, na, nb);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-4xl px-5 pb-24 pt-32 sm:px-8" data-testid="compare-page">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">Compare</p>
        <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50 sm:text-5xl">See how two patterns meet</h1>
        <p className="mt-3 max-w-2xl text-base text-slate-400">Paste two result IDs from the <span className="text-teal-300">same assessment</span> — yours and a partner's, or two of your own over time. We'll map the dynamic between them. (Find a result ID at the end of any result link, or use a shared result link.)</p>

        <form onSubmit={submit} className="mt-8 grid gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-6 sm:grid-cols-2" data-testid="compare-form">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">First result</label>
            <input value={na} onChange={(e) => setNa(e.target.value)} placeholder="Label (e.g. You)" className="mt-2 w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-2.5 text-sm text-slate-200 outline-none focus:border-teal-500" data-testid="compare-na" />
            <input value={a} onChange={(e) => setA(e.target.value)} placeholder="res_xxxxxxxx" className="mt-2 w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-2.5 font-mono text-sm text-slate-200 outline-none focus:border-teal-500" data-testid="compare-a" />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Second result</label>
            <input value={nb} onChange={(e) => setNb(e.target.value)} placeholder="Label (e.g. Partner)" className="mt-2 w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-2.5 text-sm text-slate-200 outline-none focus:border-teal-500" data-testid="compare-nb" />
            <input value={b} onChange={(e) => setB(e.target.value)} placeholder="res_yyyyyyyy" className="mt-2 w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-2.5 font-mono text-sm text-slate-200 outline-none focus:border-teal-500" data-testid="compare-b" />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={loading || !a || !b} className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-400 disabled:opacity-40" data-testid="compare-submit">
              <GitDiff size={16} weight="bold" /> {loading ? "Comparing…" : "Compare"}
            </button>
          </div>
        </form>

        {error && <p className="mt-4 rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300" data-testid="compare-error">{error}</p>}

        {data && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="compare-result">
            {!data.same_assessment && (
              <p className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-300">These two results are from different assessments, so we can show them side-by-side but can't generate a relational read. For the full dynamic, compare two results from the same assessment.</p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {[{ side: data.a, label: data.label_a }, { side: data.b, label: data.label_b }].map(({ side, label }, i) => (
                <div key={i} className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid={`compare-side-${i}`}>
                  <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-25 blur-2xl" style={{ background: side.primary.color }} />
                  <p className="relative text-xs uppercase tracking-wider text-slate-500">{label}</p>
                  <p className="relative mt-1 text-[11px] text-slate-600">{side.assessment_name}</p>
                  <h3 className="relative mt-3 font-heading text-2xl font-light" style={{ color: side.primary.color }}>{side.primary.name}</h3>
                  <p className="relative mt-2 text-sm italic text-slate-400">&quot;{side.primary.tagline}&quot;</p>
                  <div className="relative mt-4 space-y-2">
                    {side.breakdown.slice(0, 4).map((bk) => (
                      <div key={bk.key}>
                        <div className="flex justify-between text-[11px]"><span className="text-slate-400">{bk.name}</span><span className="font-mono text-slate-600">{bk.score}</span></div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full" style={{ width: `${bk.score}%`, background: bk.color }} /></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {data.synthesis && (
              <div className="mt-6 space-y-4">
                <div className="rounded-2xl border border-teal-500/30 bg-teal-500/5 p-6" data-testid="compare-dynamic">
                  <div className="flex items-center gap-2"><Users size={18} weight="duotone" className="text-teal-400" /><h3 className="font-heading text-base font-medium text-slate-100">The dynamic between you</h3></div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-200">{data.synthesis.dynamic}</p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <Panel icon={Sparkle} title="What works" tone="teal" items={data.synthesis.strengths} />
                  <Panel icon={Warning} title="Watch for" tone="amber" items={data.synthesis.tensions} />
                </div>
                {data.synthesis.advice?.length > 0 && (
                  <div className="rounded-2xl border p-6" style={{ borderColor: "#14b8a640", background: "#14b8a60d" }} data-testid="compare-advice">
                    <div className="flex items-center gap-2"><Lightbulb size={18} weight="duotone" className="text-teal-400" /><h3 className="font-heading text-base font-medium text-slate-100">How to navigate it well</h3></div>
                    <ul className="mt-3 space-y-2">{data.synthesis.advice.map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-200"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" /> {s}</li>)}</ul>
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 text-center">
              <Link to="/#all" className="inline-flex items-center gap-2 text-sm font-medium text-teal-400 hover:text-teal-300">Take an assessment to compare <ArrowRight size={14} weight="bold" /></Link>
            </div>
          </motion.div>
        )}

        <div className="mt-12"><Disclaimer /></div>
      </div>
      <Footer />
    </div>
  );
}

function Panel({ icon: Icon, title, items, tone }) {
  const toneClass = tone === "amber" ? "text-amber-400 bg-amber-500/10" : "text-teal-400 bg-teal-500/10";
  const dot = tone === "amber" ? "bg-amber-500" : "bg-teal-400";
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
      <div className="flex items-center gap-3"><span className={`flex h-9 w-9 items-center justify-center rounded-lg ${toneClass}`}><Icon size={18} weight="duotone" /></span><h3 className="font-heading text-base font-medium text-slate-100">{title}</h3></div>
      <ul className="mt-4 space-y-2.5">{(items || []).map((s, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-300"><span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} /> {s}</li>)}</ul>
    </div>
  );
}
